import {
    useQuery,
    useMutation,
    QueryClient,
    UseMutationResult,
    UseQueryResult,
    UseQueryOptions,
} from '@tanstack/react-query';
import {
    HttpContentWithoutData,
    RQAPICall,
    RQAPICallWithData,
} from 'store/APIClient';

import i18n from 'i18n';
import { clearMantyGraphQueryApiServiceWorkerCache } from 'routes/ImportCsv/csvCacheHelpers';
import { ImportForm, TableMetadata } from 'routes/Cellar/types';

type TableDataMutationParams = {
    fileName: string;
    fileData: File;
    shouldReplace: boolean;
};
/**
 * Update the data of a given table by either:
 * - deleting existing data and replace it (shouldReplace = True)
 * - append new data to existing one
 */
export const useTableDataMutation = (
    queryClient: QueryClient,
    tableName: string | null,
    onSuccess: () => void,
    onError: (error: Error) => void,
): UseMutationResult<void, Error> =>
    useMutation({
        mutationFn: async ({
            fileName,
            fileData,
            shouldReplace,
        }: TableDataMutationParams) => {
            const data = new FormData();
            data.set('fileName', fileName);
            data.set('fileData', fileData);
            data.set('appendDelete', shouldReplace.toString());
            await RQAPICall({
                endpoint: `extractors/upload_csv_file/${tableName}`,
                method: 'POST',
                body: data,
                isNotJson: true,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [`getTableData-${tableName}`],
            });
            queryClient.invalidateQueries({
                queryKey: ['getUploadedFiles', tableName],
            });
            onSuccess();
            clearMantyGraphQueryApiServiceWorkerCache();
        },
        onError,
    });

type TableDataCreationParams = {
    fileName: string;
    fileData: File;
};

export const useTableCreationMutation = (
    queryClient: QueryClient,
    onSuccess: (arg1: TableMetadata) => void,
    onError: (error: Error) => void,
): UseMutationResult<TableMetadata, Error> =>
    useMutation({
        mutationFn: async ({
            fileName,
            fileData,
            isPrivate,
            columns,
        }: TableDataCreationParams & ImportForm) => {
            const data = new FormData();
            data.set('fileName', fileName);
            data.set('fileData', fileData);
            data.set('isPrivate', isPrivate.toString());
            data.set('columns', JSON.stringify(columns));
            const response = await RQAPICallWithData<TableMetadata>({
                endpoint: 'schema/extension/table',
                method: 'POST',

                body: data,
                isNotJson: true,
            });
            return response.content.data;
        },
        onSuccess: (tableMetadata) => {
            onSuccess(tableMetadata);
            const { name: tableName } = tableMetadata;
            queryClient.invalidateQueries({
                queryKey: ['getAutoGeneratedTables'],
            });
            queryClient.invalidateQueries({
                queryKey: [`getTableData-${tableName}`],
            });
            queryClient.invalidateQueries({
                queryKey: ['getUploadedFiles', tableName],
            });
            clearMantyGraphQueryApiServiceWorkerCache();
            i18n.reloadResources();
        },
        onError: (error: Error) => {
            onError(error);
        },
    });

/**
 * Delete the content of the table
 */
export const useDeleteTableMutation = (
    queryClient: QueryClient,
    tableName: string | null,
    onSuccess: () => void,
    onError: (error: Error) => void,
): UseMutationResult<void, Error> =>
    useMutation({
        mutationFn: async () => {
            await RQAPICall({
                endpoint: `schema/extension/table/${tableName}/empty`,
                method: 'PATCH',
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [`getTableData-${tableName}`],
            });
            onSuccess();
            clearMantyGraphQueryApiServiceWorkerCache();
        },
        onError,
    });

/**
 * Delete the content and archive CSV
 */
export const useArchiveCSVMutation = (
    queryClient: QueryClient,
    tableName: string | null,
    onSuccess: () => void,
    onError: (error: Error) => void,
): UseMutationResult<void, Error> =>
    useMutation({
        mutationFn: async () => {
            await RQAPICall({
                endpoint: `schema/extension/table/${tableName}/archive`,
                method: 'PATCH',
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['getAutoGeneratedTables'],
            });
            onSuccess();
            clearMantyGraphQueryApiServiceWorkerCache();
        },
        onError,
    });

/**
 * Drop the table
 */
export const useDropTableMutation = (
    queryClient: QueryClient,
    tableName: string | null,
    onSuccess: (content: HttpContentWithoutData) => void,
    onError: (error: Error) => void,
): UseMutationResult<HttpContentWithoutData, Error> =>
    useMutation({
        mutationFn: async () => {
            const res = await RQAPICall<HttpContentWithoutData>({
                endpoint: `schema/extension/table/${tableName}`,
                method: 'DELETE',
            });
            return res.content;
        },
        onSuccess: (content: HttpContentWithoutData) => {
            queryClient.invalidateQueries({
                queryKey: ['getAutoGeneratedTables'],
            });
            onSuccess(content);
            clearMantyGraphQueryApiServiceWorkerCache();
        },
        onError: (error) => onError(error),
    });

/**
 * Get the autogenerated csv tables
 */
export const useGetAutoGeneratedTables = (
    params: Pick<
        UseQueryOptions<TableMetadata[], Error>,
        'enabled' | 'initialData'
    >,
): UseQueryResult<TableMetadata[], Error> =>
    useQuery({
        queryKey: ['getAutoGeneratedTables'],
        queryFn: async () => {
            const response = await RQAPICallWithData<TableMetadata[]>({
                method: 'GET',
                endpoint: `schema/extension/tables?type=csv`,
            });
            return response.content.data;
        },

        refetchOnWindowFocus: false,
        ...params,
    });
